/* global Office */
import * as querystring from 'querystring-es3';
import * as RootActions from '../actions/index';
import { navigatingFolders } from '../actions/folders';
import { addingTime, showTimeReminder } from '../actions/time';
import { requestLogout, logoutSuccess, logoutFailure } from '../actions/auth';
import { logoutUser } from '../../api/emailsApi/users';
import { getSalesforcePkce } from '../../api/emailsApi/client';
import officeApi from '../../api/officeApi';
import { Buffer } from 'buffer/';
import { fetchToken } from '../../api/fetchToken';

const getUserId = async () => {
  const token = await fetchToken();
  if (!token) {
    throw Error('No token received from outlook.');
  }
  const tokenParts = token.split('.');
  const payload = JSON.parse(Buffer.from(tokenParts[1], 'base64').toString());
  const jwksUri = 'https://login.microsoftonline.com/common/discovery/keys';
  return Buffer.from(`${payload.oid}.${jwksUri}`).toString('base64');
};

export const generateSalesforceOAuthUrl = async (isSandbox) => {
  const oid = await getUserId();
  if (!oid) {
    throw Error('Unable to fetch outlook user. Please refresh and try again.');
  }
  const emailAddress = await officeApi.getUserInfo().emailAddress;
  const redirectUrl = `${process.env.PLUGIN_URL}/auth.html`;
  const [loginUrl, clientId] = isSandbox ? ['https://test.salesforce.com', process.env.SALESFORCE_SANDBOX_CLIENT_ID] : [process.env.SALESFORCE_LOGIN_URL, process.env.SALESFORCE_CLIENT_ID];
  const oAuthUrl = `${loginUrl}/services/oauth2/authorize`;

  // Fetch PKCE values from the backend (the signing key never leaves the server).
  const pkce = await getSalesforcePkce();

  const state = JSON.stringify({ oid, isSandbox, emailAddress, redirectUrl, ev: pkce.encryptedVerifier });
  const encoded = (Buffer.from(state)).toString('base64');

  return `${oAuthUrl}?${querystring.stringify(
      Object.assign({}, {
        client_id: clientId,
        redirect_uri: process.env.SALESFORCE_CALLBACK_URL,
        response_type: 'code',
        scope: 'api offline_access refresh_token',
        prompt: 'login consent',
        state: encoded,
        code_challenge: pkce.codeChallenge,
        code_challenge_method: pkce.codeChallengeMethod,
      }),
  )}`;
};

export const handleLoginDesktop = async (uri) => {
  try {
    const dialog = await officeApi.openUiDisplay(uri);
    await new Promise((resolve, reject) => {
      const dialogClosed = (e) => {
        switch (e.error) {
          case 12006:
            // This is a special case where the dialog was closed by the user or browser/script. We can ignore and wait for auth
            // if this occurs.
            break;
          default:
            console.error(e);
            return reject(new Error(`An error occurred while authenticating: ${e.message}`));
        }
        resolve();
      };

      const closeDialog = (e) => {
        if (e.message == 'Authenticated' && dialog) {
          try {
            dialog.close();
            resolve();
          } catch (e) {
            console.error(e);
            reject(e);
          }
        }
      };

      dialog.addEventHandler(Office.EventType.DialogEventReceived, dialogClosed);
      dialog.addEventHandler(Office.EventType.DialogMessageReceived, closeDialog);
    });
  } catch (e) {
    console.error(e);
    throw new Error(`An error occurred while authenticating: ${e.message}`);
  }
};

export const handleLogout = async (dispatch) => {
  dispatch(RootActions.clearAlertText());
  dispatch(RootActions.isLoading(true));
  dispatch(RootActions.setInitializationError(false));
  dispatch(requestLogout());
  const response = await logoutUser();
  if (response instanceof Error) {
    dispatch(logoutFailure(response));
  } else {
    dispatch(logoutSuccess());
    localStorage.clear();
  }
  dispatch(navigatingFolders(false));
  dispatch(addingTime(false));
  dispatch(showTimeReminder(false));
  dispatch(RootActions.isLoading(false));
};

export const handleLoginBrowser = async (url) => {
  try {
    const loginWindow = window.open(
        url,
        'Docrio Emails',
        `width=550, 
        height=750, 
        left=${(window.screen.width - 550) / 2}, 
        top=${(window.screen.height - 650) / 2}`,
    );

    const isWindowClosed = () => {
      return new Promise((resolve) => {
        const interval = setInterval(() => {
          if (loginWindow.closed) {
            resolve(clearInterval(interval));
          }
        }, 200);
      });
    };

    await isWindowClosed();
  } catch (e) {
    console.error(e);
    throw new Error(`An error occurred while authenticating: ${e.message}`);
  }
};
